import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useThrottleFn } from 'ahooks';
import { useIntl } from 'umi';
import { Modal } from 'tea-component';
import NewFeatureListItem from './components/NewFeatureListItem';
import { getNewFeatureItemKey } from '../../utils';

import type { INewFeatureItem } from '@home/types/urlCollection';

import './index.scss';

interface INewFeaturesModalProps extends IBaseProps {
  visible: boolean;
  title?: string;
  batchSize?: number;
  items: INewFeatureItem[];
  onClose?: () => void;
  onItemClick?: (item: INewFeatureItem) => void;
}

const NewFeaturesModal: React.FC<INewFeaturesModalProps> = (props) => {
  const { style, className, visible, title, batchSize = 20, items, onClose, onItemClick } = props;
  const { formatMessage } = useIntl();
  const titleText =
    title ||
    formatMessage({
      id: 'dbh_url_collection_new_features_title',
      defaultMessage: 'New Features',
    });
  const [visibleCount, setVisibleCount] = useState(batchSize);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setVisibleCount(batchSize);
  }, [batchSize, visible]);

  const visibleItems = useMemo(() => {
    return items.slice(0, visibleCount);
  }, [items, visibleCount]);

  const { run: handleScrollThrottled } = useThrottleFn(
    (target: HTMLDivElement) => {
      const isReachBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 20;

      if (!isReachBottom || visibleCount >= items.length) {
        return;
      }

      setVisibleCount((currentValue) => {
        return Math.min(currentValue + batchSize, items.length);
      });
    },
    { wait: 100 },
  );

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    handleScrollThrottled(event.currentTarget);
  };

  return (
    <Modal
      style={style}
      className={classnames('dbh-my-shortcuts-and-new-features-card-common-modal dbh-new-features-modal', className)}
      visible={visible}
      caption={titleText}
      onClose={onClose}
    >
      <Modal.Body>
        <div className="dbh-new-features-modal__list" onScroll={handleScroll}>
          {visibleItems?.map((item) => {
            return <NewFeatureListItem key={getNewFeatureItemKey(item)} item={item} onClick={onItemClick} />;
          })}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default NewFeaturesModal;
